import { useEffect, useRef } from 'react'
import {
  moveLeft, moveRight, moveUp, moveDown, pause, tryMoveTo
} from '../lib/behaviours'
import { useCollision } from '../hooks/use-collision'
import { usePosition } from '../hooks/use-position'
import { useWorld } from '../hooks/use-world'
import { usePathfinding } from '../hooks/use-pathfinding'
import { useWander } from '../hooks/use-wander'
import { useNpcDialog } from '../hooks/use-npc-dialog'
import { usePursuit } from '../hooks/use-pursuit'
import { distanceTo } from '../lib/geometry'
import {
  tileBoundaryToPixels, isOutOfBounds,
  getActiveBoundary, hasTimedBoundary, msUntilHourChange,
} from '../lib/boundary'
import { GRID_SIZE, NPC_TRIGGER_RANGE, NPC_DIALOG_RANGE } from '../config/constants'

const wanders = [moveLeft, moveRight, moveUp, moveDown, pause, pause, pause]

export const NPC = ({
  x = 60,
  y = 60,
  width = GRID_SIZE,
  height = GRID_SIZE,
  config = {},
  ...rest
}) => {
  const cfg = {
    stride: 1,
    solid: true,
    npc: true,
    ...config,
    entityType: 'npc'
  }

  const activeBoundaryRef  = useRef(getActiveBoundary(cfg.boundary, cfg.timezone))
  const pixelBoundaryRef   = useRef(tileBoundaryToPixels(activeBoundaryRef.current, width, height))
  const isExitingRef       = useRef(false)
  const exitTargetRef      = useRef(null)
  const hourTimerRef       = useRef(null)

  // Normalise legacy single-dialog format to the new dialogs array
  const dialogs = cfg.dialogs
    ?? (cfg.dialog ? [{ lines: cfg.dialog, repeat: false }] : [{ lines: ['Hello, adventurer!'], repeat: false }])

  // Each interaction area: a trigger tile, an optional separate spot the NPC
  // walks to (falls back to the tile itself), and its own dialog pool
  // (falls back to the NPC's default dialogs when unset).
  const areas = (cfg.interactionAreas || [])
    .filter((a) => a.tile)
    .map((a) => {
      const tile = { x: a.tile.x * GRID_SIZE, y: a.tile.y * GRID_SIZE }
      return {
        id: a.id,
        tile,
        approachDest: a.npc_position
          ? { x: a.npc_position.x * GRID_SIZE, y: a.npc_position.y * GRID_SIZE }
          : tile,
        dialogs: a.dialogs?.length ? a.dialogs : dialogs,
      }
    })

  const approachDialogsById = Object.fromEntries(areas.map((a) => [a.id, a.dialogs]))

  const approachingRef    = useRef(false)
  const approachTargetRef = useRef(null)
  const activeAreaRef     = useRef(null)

  const [position, update] = usePosition(x, y, width, height, cfg)
  const posRef = useRef(position)
  useEffect(() => { posRef.current = position }, [position])

  const { frame, spritesRef, addSprite, pendingLadderChoiceRef, currentLevel } = useWorld()
  cfg.level = currentLevel

  const { pausedRef, playerLeftZoneRef, shopOpenRef, handleDialogFrame, closeShopOnLeave } =
    useNpcDialog({ cfg, positionId: position.id, dialogs, approachDialogsById })

  const { isColliding } = useCollision()
  const { findPath } = usePathfinding()
  const { wanderStep } = useWander({ behaviors: wanders, cfg, pixelBoundaryRef, isColliding, update })
  const { getWaypoint: getApproachWaypoint, reportMoveResult: reportApproach, resetPursuit: resetApproach } =
    usePursuit({ findPath, width, height, stride: cfg.stride })

  // If NPC has a timed boundary and is currently inactive, start hidden+passable.
  // Runs after usePosition's mount effect so the sprite is registered before we hide it.
  useEffect(() => {
    if (hasTimedBoundary(cfg.boundary) && !activeBoundaryRef.current) {
      const sp = spritesRef.current[position.id]?.position
      if (sp) addSprite({ ...sp, hidden: true, config: { ...sp.config, solid: false, npc: false } })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const move = (tx, ty, pos) => tryMoveTo(tx, ty, pos, cfg.stride, isColliding, update)

  useEffect(() => {
    // ── Dialog / interaction ─────────────────────────────────────────────────
    const sp = spritesRef.current[position.id]?.position
    if (sp) handleDialogFrame(sp)
    if (pausedRef.current) approachingRef.current = false

    // Check player distance before movement gate so shop closes even while NPC is frozen
    if (activeAreaRef.current && shopOpenRef.current) {
      const player = spritesRef.current?.player?.position
      if (player && distanceTo(player, activeAreaRef.current.tile) >= NPC_TRIGGER_RANGE) closeShopOnLeave()
    }

    // ── Movement ─────────────────────────────────────────────────────────────
    if (pausedRef.current || shopOpenRef.current || cfg.stationary || pendingLadderChoiceRef.current) return

    const pos    = posRef.current
    const player = spritesRef.current?.player?.position

    if (areas.length && player) {
      const inRange = areas.filter((a) => distanceTo(player, a.tile) < NPC_TRIGGER_RANGE)
      if (inRange.length === 0) {
        playerLeftZoneRef.current = true
        closeShopOnLeave()
      }

      if (!approachingRef.current && playerLeftZoneRef.current && inRange.length > 0) {
        activeAreaRef.current = inRange.reduce((best, a) =>
          distanceTo(player, a.tile) < distanceTo(player, best.tile) ? a : best)
        approachingRef.current = true
        playerLeftZoneRef.current = false
        resetApproach()
        approachTargetRef.current = null
      }
    }

    if (approachingRef.current && activeAreaRef.current) {
      const area = activeAreaRef.current
      const approachDest = area.approachDest
      const b = pixelBoundaryRef.current
      if (!approachTargetRef.current) {
        if (b) {
          const neighbors = [
            { x: approachDest.x - GRID_SIZE, y: approachDest.y },
            { x: approachDest.x + GRID_SIZE, y: approachDest.y },
            { x: approachDest.x, y: approachDest.y - GRID_SIZE },
            { x: approachDest.x, y: approachDest.y + GRID_SIZE },
          ].filter(n => !isOutOfBounds(n, b))
          const cur = posRef.current
          approachTargetRef.current = neighbors.length > 0
            ? neighbors.reduce((best, n) => distanceTo(n, cur) < distanceTo(best, cur) ? n : best)
            : { x: Math.min(Math.max(approachDest.x, b.minX), b.maxX),
                y: Math.min(Math.max(approachDest.y, b.minY), b.maxY) }
        } else {
          approachTargetRef.current = approachDest
        }
      }
      const approachTarget = approachTargetRef.current

      const { waypoint, done } = getApproachWaypoint(pos, approachTarget, {
        arrivalThreshold: NPC_DIALOG_RANGE,
        finalTarget: approachDest,
        stuckThreshold: 10,
      })

      if (done) {
        const playerDist = player ? distanceTo(player, area.tile) : Infinity
        if (playerDist <= NPC_DIALOG_RANGE) {
          const sp = spritesRef.current[position.id]?.position
          if (sp && !sp.interacting) addSprite({ ...sp, interacting: true, approachDialog: true, areaId: area.id })
        }
        return
      }

      if (waypoint !== null) {
        if (!isOutOfBounds(waypoint, b)) {
          reportApproach(move(waypoint.x, waypoint.y, pos))
        }
        return
      }

      // Path exhausted — take final step directly to destination (no boundary check)
      if (!move(approachDest.x, approachDest.y, pos)) resetApproach()
      return
    }

    if (isExitingRef.current) {
      const target = exitTargetRef.current
      if (target) {
        const dist = Math.hypot(pos.x - target.x, pos.y - target.y)
        if (dist < cfg.stride + 2) {
          const sp = spritesRef.current[position.id]?.position
          if (sp) addSprite({ ...sp, hidden: true, config: { ...sp.config, solid: false, npc: false } })
          isExitingRef.current = false
        } else {
          move(target.x, target.y, pos)
        }
      }
      return
    }

    wanderStep(pos)
  }, [frame])

  useEffect(() => {
    if (!hasTimedBoundary(cfg.boundary)) return

    const onHourChange = () => {
      const newActive  = getActiveBoundary(cfg.boundary, cfg.timezone)
      const newPixel   = tileBoundaryToPixels(newActive, width, height)
      const hadBoundary = !!pixelBoundaryRef.current

      if (!newPixel && hadBoundary && !isExitingRef.current) {
        isExitingRef.current = true
        const last = pixelBoundaryRef.current
        const pos  = posRef.current
        const cx   = (last.minX + last.maxX) / 2
        const cy   = (last.minY + last.maxY) / 2
        const dx   = pos.x - cx || 1
        const dy   = pos.y - cy || 0
        const len  = Math.hypot(dx, dy)
        const dist = Math.max(last.maxX - last.minX, last.maxY - last.minY) + 120
        exitTargetRef.current = {
          x: cx + (dx / len) * dist,
          y: cy + (dy / len) * dist,
        }
      } else if (newPixel) {
        isExitingRef.current = false
        exitTargetRef.current = null
        const sp = spritesRef.current[position.id]?.position
        if (sp?.hidden) {
          addSprite({ ...sp, hidden: false, config: { ...sp.config, solid: true, npc: true } })
        }
      }

      activeBoundaryRef.current = newActive
      pixelBoundaryRef.current  = newPixel
      hourTimerRef.current = setTimeout(onHourChange, msUntilHourChange(cfg.timezone) + 100)
    }

    hourTimerRef.current = setTimeout(onHourChange, msUntilHourChange(cfg.timezone) + 100)
    return () => clearTimeout(hourTimerRef.current)
  }, [])

  return null
}
